import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { identifyPostHogUser } from '../posthog';
import { markApplicantSessionStarted } from '../utils/applicantSession';
import { formatApiDetail } from '../utils/apiError';
import {
  LOGIN_PATH,
  getUserRole,
  isOrgWorkbenchAdmin,
  rememberAuthPortal,
  resolvePostLoginPathAsync,
} from '../utils/postLoginNavigation';
import './CustomerSignup.css';

const APPLICANT_ENTRY_INTENT_KEY = 'mvp_applicant_entry_intent';

function readOAuthHashParams() {
  try {
    const raw = String(window.location.hash || '').replace(/^#/, '');
    if (!raw) return new URLSearchParams();
    return new URLSearchParams(raw);
  } catch {
    return new URLSearchParams();
  }
}

/**
 * OAuth callback after Google/Apple.
 *
 * Primary: POST /auth/oauth/consume reads the httpOnly handoff cookie set by
 * the API callback (survives ALB; JWT never in the URL).
 * Fallback: ?token= / #token= for complete-profile magic links.
 * Multi-production: consume may return needs_project_selection + pick_token.
 */
export default function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [errorDetail, setErrorDetail] = useState('');
  const [pickToken, setPickToken] = useState('');
  const [projectChoices, setProjectChoices] = useState([]);
  const [pickEmail, setPickEmail] = useState('');
  const [pickBusy, setPickBusy] = useState(false);
  const [pickError, setPickError] = useState('');
  const [oauthIntent, setOauthIntent] = useState('login');

  const finishWithSession = useCallback(async (token, user, intent) => {
    localStorage.setItem('authToken', token);
    try {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?intent=${encodeURIComponent(intent)}`,
      );
    } catch {
      // ignore
    }

    if (!user) {
      const res = await mvpApi.getMeWithToken(token);
      user = res.data?.user || res.data;
    }
    if (user) localStorage.setItem('userData', JSON.stringify(user));
    identifyPostHogUser(user);

    const role = getUserRole(user);
    const workbenchAdmin = isOrgWorkbenchAdmin(user);

    rememberAuthPortal(workbenchAdmin ? 'password' : 'oauth', user);

    if (workbenchAdmin) {
      const dest = await resolvePostLoginPathAsync({
        user,
        method: 'password',
        intent: 'login',
      });
      navigate(dest, { replace: true });
      return;
    }

    if (role === 'admin' || role === 'advisor') {
      const dest = await resolvePostLoginPathAsync({
        user,
        method: 'oauth',
        intent: intent || 'login',
      });
      navigate(dest, { replace: true });
      return;
    }

    markApplicantSessionStarted();
    sessionStorage.setItem(
      APPLICANT_ENTRY_INTENT_KEY,
      intent === 'signup' ? 'signup' : 'login',
    );

    if (intent === 'signup') {
      try {
        sessionStorage.removeItem('mvp_sso_signup_step1');
      } catch {
        // ignore
      }
      navigate('/signup/sso-details', { replace: true });
      return;
    }

    const dest = await resolvePostLoginPathAsync({
      user,
      method: 'oauth',
    });
    navigate(dest, { replace: true });
  }, [navigate]);

  const handleSelectProject = useCallback(async (choice) => {
    if (!pickToken || pickBusy) return;
    setPickError('');
    setPickBusy(true);
    try {
      const res = await mvpApi.selectOAuthProject(pickToken, choice.project_id);
      const token = String(res?.data?.token || '').trim();
      const user = res?.data?.user || null;
      if (!token) {
        throw new Error('Missing session after production selection.');
      }
      await finishWithSession(token, user, oauthIntent || 'login');
    } catch (err) {
      setPickError(
        formatApiDetail(err?.response?.data?.detail)
          || err?.message
          || 'Could not open that production. Please try again.',
      );
      setPickBusy(false);
    }
  }, [pickToken, pickBusy, oauthIntent, finishWithSession]);

  useEffect(() => {
    let cancelled = false;

    async function finishOAuth() {
      const hashParams = readOAuthHashParams();
      const urlToken = (searchParams.get('token') || hashParams.get('token') || '').trim();
      const error = searchParams.get('error') || searchParams.get('e') || hashParams.get('error');
      const intent = (
        searchParams.get('intent')
        || hashParams.get('intent')
        || 'login'
      ).trim();
      if (!cancelled) setOauthIntent(intent);
      const oauthCodeWithoutToken = searchParams.get('code') && !urlToken;

      if (error || oauthCodeWithoutToken) {
        if (!cancelled) {
          setErrorDetail(error || 'oauth_missing_token');
          setStatus('error');
        }
        return;
      }

      let token = '';
      let user = null;
      let handoff = null;

      // 1) Cookie handoff from Google/Apple API callback (preferred).
      try {
        const res = await mvpApi.consumeOAuthHandoff();
        handoff = res?.data || null;
        if (handoff?.needs_project_selection) {
          const projects = handoff.projects || [];
          const nextPick = String(handoff.pick_token || '').trim();
          if (!nextPick || !projects.length) {
            if (!cancelled) {
              setErrorDetail('oauth_missing_token');
              setStatus('error');
            }
            return;
          }
          if (cancelled) return;
          if (projects.length === 1) {
            try {
              const sel = await mvpApi.selectOAuthProject(nextPick, projects[0].project_id);
              const t = String(sel?.data?.token || '').trim();
              if (!t) throw new Error('missing token');
              if (cancelled) return;
              await finishWithSession(t, sel?.data?.user || null, intent || 'login');
            } catch (err) {
              if (cancelled) return;
              setPickToken(nextPick);
              setProjectChoices(projects);
              setPickEmail(handoff.email || '');
              setPickError(
                formatApiDetail(err?.response?.data?.detail)
                  || 'Could not open that production. Please choose one.',
              );
              setStatus('pick');
            }
            return;
          }
          setPickToken(nextPick);
          setProjectChoices(projects);
          setPickEmail(handoff.email || '');
          setStatus('pick');
          return;
        }
        token = String(handoff?.token || '').trim();
        user = handoff?.user || null;
      } catch {
        // Fall through to URL token (complete-profile / legacy).
      }

      // 2) URL token fallback.
      if (!token) {
        token = urlToken;
      }

      if (!token) {
        navigate(`${LOGIN_PATH}?error=oauth_missing_token`, { replace: true });
        return;
      }

      try {
        if (cancelled) return;
        await finishWithSession(token, user, intent);
      } catch (err) {
        if (cancelled) return;
        try {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
        } catch {
          // ignore
        }
        const detail = err?.response?.data?.detail;
        setErrorDetail(typeof detail === 'string' ? detail : 'session_failed');
        setStatus('error');
      }
    }

    finishOAuth();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams, finishWithSession]);

  if (status === 'pick') {
    return (
      <div className="psu-root">
        <div className="psu-shell">
          <div className="psu-card">
            <div className="psu-body">
              <div className="psu-eyebrow">Sign in</div>
              <div className="psu-h">Which production?</div>
              <div className="psu-sub">
                {pickEmail
                  ? `${pickEmail} is registered on more than one production. Choose where you want to continue.`
                  : 'This account is registered on more than one production. Choose where you want to continue.'}
              </div>
              <div className="psu-ch-list" role="list">
                {projectChoices.map((choice) => (
                  <button
                    key={choice.project_id}
                    type="button"
                    className="psu-ch"
                    role="listitem"
                    disabled={pickBusy}
                    onClick={() => handleSelectProject(choice)}
                  >
                    <div className="psu-ch-ic" aria-hidden>🎬</div>
                    <div className="psu-ch-tx">
                      <div className="psu-ch-l">{choice.project_name}</div>
                      <div className="psu-ch-v">
                        {[choice.organization_name, 'registered'].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <span className="psu-ch-radio" aria-hidden />
                  </button>
                ))}
              </div>
              {pickError ? <p className="psu-error">{pickError}</p> : null}
              <p className="psu-sub" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="psu-change"
                  onClick={() => navigate(LOGIN_PATH, { replace: true })}
                >
                  Back to sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#F0F5F9' }}>
        <p>{t('authCallback.error')}</p>
        {errorDetail ? (
          <p style={{ marginTop: 8, fontSize: 13, color: '#7A90A8' }}>{errorDetail}</p>
        ) : null}
        <button
          type="button"
          onClick={() => navigate(LOGIN_PATH, { replace: true })}
          style={{ marginTop: 16, padding: '10px 20px', cursor: 'pointer' }}
        >
          {t('authCallback.goHome')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, textAlign: 'center', color: '#7A90A8' }}>
      {t('signingIn')}
    </div>
  );
}
